/**
 * Portal Manta · SharePoint Loader
 *
 * Tenta carregar os dados dos lotes 3/4/5 do SharePoint via a Netlify
 * Function `/.netlify/functions/sharepoint-data`. Se falhar (offline,
 * env vars não configuradas, 401 etc.), retorna null para que o portal
 * use os DADOS embutidos como fallback.
 *
 * Flags em runtime:
 *   window.MANTA_DISABLE_SHAREPOINT = true       (desliga)
 *   localStorage.setItem('manta-disable-sharepoint', '1')
 *   window.MANTA_SP_TIMEOUT_MS = 60000           (custom timeout)
 *
 * Para forçar recarga (bypass cache do CDN/Function/browser):
 *   window.recarregarDoSharePoint()              ← exposto após primeiro load
 */

const ENDPOINT = '/.netlify/functions/sharepoint-data';

function isDisabled() {
  if (typeof window === 'undefined') return true;
  if (window.MANTA_DISABLE_SHAREPOINT === true) return true;
  try {
    if (window.localStorage?.getItem('manta-disable-sharepoint') === '1') return true;
  } catch (_) { /* localStorage indisponível */ }
  return false;
}

function getTimeout() {
  const t = (typeof window !== 'undefined' && Number(window.MANTA_SP_TIMEOUT_MS)) || 30000;
  return t > 0 ? t : 30000;
}

/**
 * @param {object} [opts]
 * @param {boolean} [opts.refresh] - se true, bypassa cache do CDN e da Function.
 * @returns {Promise<{lotes: object, meta: object} | null>}
 */
export async function carregarLotesDoSharePoint(opts = {}) {
  if (isDisabled()) {
    console.info('[SharePoint] desativado (flag ou localStorage)');
    return null;
  }

  const refresh = !!opts.refresh;

  // Cachebust no querystring para furar CDN. Também usa cache:'no-store' no fetch.
  const url = refresh
    ? `${ENDPOINT}?refresh=1&_ts=${Date.now()}`
    : `${ENDPOINT}?_ts=${Date.now()}`;

  const ac = new AbortController();
  const to = setTimeout(() => ac.abort(), getTimeout());

  try {
    const r = await fetch(url, {
      signal: ac.signal,
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!r.ok) {
      console.warn(`[SharePoint] HTTP ${r.status} · usando dados embutidos`);
      return null;
    }
    const j = await r.json();
    if (!j || !j.lotes || typeof j.lotes !== 'object') {
      console.warn('[SharePoint] resposta sem campo `lotes` · fallback');
      return null;
    }
    const keys = Object.keys(j.lotes);
    if (keys.length === 0) {
      console.warn('[SharePoint] resposta vazia · fallback');
      return null;
    }
    console.info(`[SharePoint] carregados ${keys.length} lotes:`, keys.join(', '),
      refresh ? '(refresh forçado)' : '');
    if (j.meta?.errors?.length) {
      console.warn('[SharePoint] alguns lotes falharam:', j.meta.errors);
    }
    return j;
  } catch (err) {
    if (err.name === 'AbortError') {
      console.warn('[SharePoint] timeout após ' + getTimeout() + 'ms · fallback');
    } else {
      console.warn('[SharePoint] erro:', err.message || err, '· fallback');
    }
    return null;
  } finally {
    clearTimeout(to);
  }
}
